import {
  Button,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Avatar,
  Heading,
  ModalCloseButton,
  FormControl,
  FormLabel,
  ButtonGroup,
  Icon
} from '@homebotapp/hb-react-component-catalog'
import type { ButtonProps } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerPhotoUri,
  selectScheduleACallUri
} from '../../../store/selectors/customerProfile'
import { VerificationFlowProvider, VerificationFlowManager, useVerificationFlow } from '../../shared/VerificationFlow'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership
} from '../../../store/selectors/clientTeamMemberships'
import { TopicKey } from '../../../constants/customerMessages'
import { PartnerType, FlowType } from '../../shared/VerificationFlow/types'

const MSG = defineMessages({
  buyerPerksDm: {
    id: 'GenericVerificationDirectMessage.cta',
    defaultMessage: 'Ask {name} for details'
  },
  modalTitle: {
    id: 'GenericVerificationDirectMessage.modalTitle',
    defaultMessage: 'Send a message to {name}'
  },
  messagePlaceholder: {
    id: 'GenericVerificationDirectMessage.messagePlaceholder',
    defaultMessage: 'Type your message here...'
  },
  send: {
    id: 'GenericVerificationDirectMessage.send',
    defaultMessage: 'Send'
  },
  cancel: {
    id: 'GenericVerificationDirectMessage.cancel',
    defaultMessage: 'Cancel'
  },
  scheduleCall: {
    id: 'GenericVerificationDirectMessage.scheduleCall',
    defaultMessage: 'Schedule a call'
  }
})

export interface GenericVerificationDirectMessageProps {
  defaultTopicKey: TopicKey
  verifiedTopicKey: TopicKey
  message?: string
  buttonText?: string
  includePrequal?: boolean
  showModal?: boolean
  modalTitle?: string
  messagePlaceholder?: string
  partnerType?: PartnerType
  flowType?: FlowType
  buttonProps?: Partial<ButtonProps> & {
    showAvatar?: boolean
  }
}

// Internal component that uses the verification flow
const GenericVerificationDirectMessageInternal = ({
  defaultTopicKey,
  verifiedTopicKey,
  message = 'Can you tell me more about this listing gallery?',
  buttonText,
  includePrequal = false,
  showModal = false,
  modalTitle,
  messagePlaceholder,
  partnerType = 'LoanOfficer',
  flowType = 'finance',
  buttonProps = {}
}: GenericVerificationDirectMessageProps) => {
  const intl = useIntl()
  const { startVerification } = useVerificationFlow()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState(message)

  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)
  const scheduleACallUri = useSelector(selectScheduleACallUri)

  const handleButtonClick = () => {
    if (showModal) {
      setIsModalOpen(true)
    } else {
      startVerification(message, verifiedTopicKey, defaultTopicKey, partnerType, flowType)
    }
  }

  const handleModalSend = () => {
    setIsModalOpen(false)
    startVerification(modalMessage, verifiedTopicKey, defaultTopicKey, partnerType, flowType)
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setModalMessage(message) // Reset to original message
  }

  const handleScheduleCall = () => {
    if (scheduleACallUri) {
      window.open(scheduleACallUri, '_blank', 'noopener,noreferrer')
    }
    setIsModalOpen(false)
  }

  const displayText = buttonText || intl.formatMessage(MSG.buyerPerksDm, { name: customerFirstName })
  const displayModalTitle = modalTitle || intl.formatMessage(MSG.modalTitle, { name: customerFirstName })
  const displayPlaceholder = messagePlaceholder || intl.formatMessage(MSG.messagePlaceholder)

  // Extract custom props and set defaults based on established DM component patterns
  const {
    showAvatar = true, // Most DM components show customer avatar by default
    leftIcon,
    tracking = {
      guid: '8swvuJPai8GQmXBDP3LoBz',
      ui_context: 'Buyer Verification DM',
      descriptor: showModal ? 'Open message modal' : 'Direct verification flow'
    },
    ...restButtonProps
  } = buttonProps

  // Apply button styling with proper override order
  const buttonColorScheme = restButtonProps.colorScheme || 'secondary'
  const buttonVariant = restButtonProps.variant || 'outline'
  const buttonSize = restButtonProps.size || 'md'
  const buttonWidth = restButtonProps.width || '100%'

  // Determine the left icon to use
  const getLeftIcon = () => {
    if (leftIcon) {
      return leftIcon
    }
    if (showAvatar && customerPhoto) {
      return <Image src={customerPhoto} h={6} w={6} borderRadius='50%' />
    }
    return undefined
  }

  return (
    <>
      <Flex direction='column' align='center' maxW='25rem' flexShrink={0}>
        <Button
          onClick={handleButtonClick}
          leftIcon={getLeftIcon()}
          colorScheme={buttonColorScheme}
          variant={buttonVariant}
          size={buttonSize}
          width={buttonWidth}
          tracking={tracking}
          alignItems='center'
          justifyContent='center'
          display='flex'
          {...restButtonProps}
        >
          {displayText}
        </Button>
      </Flex>

      {showModal && (
        <Modal isOpen={isModalOpen} onClose={handleModalCancel} size='md'>
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalHeader>
              <Flex gap='2.5' alignItems='center'>
                <Avatar size='sm' src={customerPhoto} showBorder={false} />
                <Heading size='sm' m='0'>
                  {displayModalTitle}
                </Heading>
              </Flex>
            </ModalHeader>
            <ModalBody py='4'>
              <FormControl>
                <FormLabel srOnly>{intl.formatMessage(MSG.messagePlaceholder)}</FormLabel>
                <Textarea
                  name='modalMessage'
                  value={modalMessage}
                  onChange={e => setModalMessage(e.target.value)}
                  placeholder={displayPlaceholder}
                  rows={4}
                  resize='vertical'
                  tracking={{
                    guid: '1swvuJPai8GQmXBDP3LoBz',
                    ui_context: 'Message Modal',
                    descriptor: 'Message textarea'
                  }}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup gap='2' alignItems='center'>
                {scheduleACallUri && (
                  <Button
                    variant='ghost'
                    colorScheme='brand'
                    size='sm'
                    onClick={handleScheduleCall}
                    tracking={{
                      guid: '9swvuJPai8GQmXBDP3LoBz',
                      ui_context: 'Message Modal',
                      descriptor: 'Schedule a call'
                    }}
                  >
                    {intl.formatMessage(MSG.scheduleCall)}
                  </Button>
                )}
                <Button
                  colorScheme='primary'
                  size='sm'
                  onClick={handleModalSend}
                  isDisabled={!modalMessage.trim()}
                  rightIcon={<Icon name='paper-plane' />}
                  tracking={{
                    guid: '0swvuJPai8GQmXBDP3LoBz',
                    ui_context: 'Message Modal',
                    descriptor: 'Send message and start verification'
                  }}
                >
                  {intl.formatMessage(MSG.send)}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export const GenericVerificationDirectMessage = (props: GenericVerificationDirectMessageProps) => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)

  const partnerType = props.partnerType || 'LoanOfficer'
  const flowType = props.flowType || 'finance'

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: true,
        initialPartnerType: partnerType,
        flowType: flowType,
        triggerOnMessageSent: true,
        loanOfficerMembership: loanOfficerMembership,
        realEstateAgentMembership: realEstateAgentMembership,
        includePrequal: props.includePrequal
      }}
    >
      <VerificationFlowManager />
      <GenericVerificationDirectMessageInternal {...props} />
    </VerificationFlowProvider>
  )
}
